/**
 * T3: Projects panel — collapsible table listing the projects that
 * compose the currently filtered forecast view, plus a per-month drawer.
 *
 * Pure helper buildProjectsTable() takes assets + filtered forecasts and
 * returns the aggregated row data. Rendered HTML lives in this same
 * module so the dashboard can call mountProjectsPanel(filtered) at the
 * end of every render cycle.
 */

import { escapeHtml } from "../utils/escapeHtml";
import { formatNumber } from "../utils/formatting";
import { normalizeValue } from "../panels/shared";
import { getState, updateState } from "../state";
import type { Asset, Forecast } from "../types/index";

export interface ProjectRow {
  project: string;
  type: string;
  countries: number;
  ocdDate: string;
  briefDate: string;
  forecast: number;
  status: string;
  variations: Array<{ name: string; ean: string; quantity: number }>;
}

export function buildProjectsTable(
  assets: Asset[],
  filteredForecasts: Forecast[],
): ProjectRow[] {
  const assetsById = new Map<string, Asset>(assets.map((a) => [a.id, a]));
  const groups = new Map<string, ProjectRow & { countrySet: Set<string> }>();

  for (const fc of filteredForecasts) {
    const asset = assetsById.get(fc.assetId);
    if (!asset) continue;
    const isParent = normalizeValue(asset.parentChild) === "parent";
    const project =
      String(asset.projectName || asset.name || asset.id || "Unknown");
    const country = String(fc.country || asset.hub || "");
    const key = project;
    let row = groups.get(key) as any;
    if (!row) {
      row = {
        project,
        type: String(asset.type || ""),
        countries: 0,
        ocdDate: String(asset.ocdDate || asset.requestedDeliveryDate || ""),
        briefDate: String(asset.briefDate || asset.dmiMadDate || ""),
        forecast: 0,
        status: isParent ? "Parent" : "Child",
        variations: [],
        countrySet: new Set<string>(),
      };
      groups.set(key, row);
    }
    row.forecast += Number(fc.quantity || 0);
    if (country) row.countrySet.add(country);
    if (!isParent) {
      row.variations.push({
        name: String(asset.productName || asset.name || asset.id),
        ean: String(asset.globalEAN || asset.id || ""),
        quantity: Number(fc.quantity || 0),
      });
    }
  }

  // Convert Set to count for each row
  const result = [...groups.values()].map((row) => {
    const { countrySet, ...rest } = row;
    return {
      ...rest,
      countries: countrySet.size,
    };
  });

  return result.sort((a, b) => a.project.localeCompare(b.project));
}

// audit C5: expanded keys live in state.ts (`expandedDashboardProjects`)
// so they survive panel switches and remounts.
function getExpanded(): Set<string> {
  const set = getState().expandedDashboardProjects;
  return set instanceof Set ? set : new Set<string>();
}
function toggleExpanded(key: string): void {
  const next = new Set(getExpanded());
  if (next.has(key)) next.delete(key);
  else next.add(key);
  updateState({ expandedDashboardProjects: next });
}

export function mountProjectsPanel(rows: ProjectRow[]): void {
  const title = document.getElementById("dashboard-projects-title");
  const body = document.getElementById("dashboard-projects-body");
  const toggle = document.getElementById("dashboard-projects-toggle");
  const wrap = document.getElementById("dashboard-projects-wrap");
  if (!title || !body) return;

  title.textContent = `Projects (${rows.length})`;

  // Bind toggle once.
  if (toggle && !toggle.dataset.boundT3) {
    toggle.dataset.boundT3 = "1";
    toggle.addEventListener("click", () => {
      if (!wrap) return;
      const hidden = wrap.classList.toggle("hidden");
      toggle.textContent = hidden ? "Show" : "Hide";
    });
  }

  if (!rows.length) {
    body.innerHTML = `<tr><td colspan="8" style="color:var(--muted)">No projects match the current filters.</td></tr>`;
    return;
  }

  const expanded = getExpanded();
  body.innerHTML = rows
    .map((row) => {
      const key = row.project;
      const isExpanded = expanded.has(key);
      const caret = row.variations.length ? (isExpanded ? "▾" : "▸") : "";
      const main = `<tr class="dp-row" data-dp-key="${escapeHtml(key)}">
        <td>${row.variations.length ? `<button class="btn btn-sm btn-ghost" data-dp-toggle="${escapeHtml(key)}" type="button">${caret}</button>` : ""}</td>
        <td>${escapeHtml(row.project)}</td>
        <td>${escapeHtml(row.type)}</td>
        <td>${row.countries}</td>
        <td>${escapeHtml(row.ocdDate)}</td>
        <td>${escapeHtml(row.briefDate)}</td>
        <td>${escapeHtml(formatNumber(row.forecast))}</td>
        <td>${escapeHtml(row.status)}</td>
      </tr>`;
      const children = isExpanded
        ? row.variations
            .map(
              (v) => `<tr class="dp-child"><td></td><td colspan="3">↳ ${escapeHtml(v.name)}</td><td colspan="2">EAN ${escapeHtml(v.ean)}</td><td>${escapeHtml(formatNumber(v.quantity))}</td><td></td></tr>`,
            )
            .join("")
        : "";
      return main + children;
    })
    .join("");

  // Bind expand handlers (delegated each render — cheap).
  body.querySelectorAll<HTMLElement>("[data-dp-toggle]").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.stopPropagation();
      const key = btn.dataset.dpToggle || "";
      toggleExpanded(key);
      mountProjectsPanel(rows);
    });
  });
}

export function showMonthDrawer(monthLabel: string, rows: ProjectRow[]): void {
  const drawer = document.getElementById("month-drawer");
  const title = document.getElementById("month-drawer-title");
  const body = document.getElementById("month-drawer-body");
  if (!drawer || !title || !body) return;

  title.textContent = `Projects for ${monthLabel} (${rows.length})`;
  body.innerHTML = rows.length
    ? rows
        .map(
          (row) => `<tr>
        <td>${escapeHtml(row.project)}</td>
        <td>${escapeHtml(row.type)}</td>
        <td>${row.countries}</td>
        <td>${escapeHtml(formatNumber(row.forecast))}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="4" style="color:var(--muted)">No projects in this month.</td></tr>`;

  drawer.classList.remove("hidden");
  const closeBtn = document.getElementById("month-drawer-close");
  if (closeBtn && !closeBtn.dataset.boundT3) {
    closeBtn.dataset.boundT3 = "1";
    closeBtn.addEventListener("click", () => drawer.classList.add("hidden"));
  }
}
